// src/app/modules/apps/call/wallboard/RealtimeCounts.tsx
import React, { useEffect, useRef, useState, useImperativeHandle, forwardRef, FC } from 'react'
import { Card } from './Card'

const apiUrl = process.env.REACT_APP_API_URL

// ===== UTILITY FUNCTIONS =====
const fmt = (n: any) => new Intl.NumberFormat().format(Number(n || 0))

// Helper: Get token from localStorage
function getApiToken() {
  try {
    const raw = localStorage.getItem('kt-auth-react-v')
    if (!raw) return ''
    const parsed = JSON.parse(raw)
    return parsed.api_token || ''
  } catch {
    return ''
  }
}

// ===== VIBRANT COMPACT PILL COMPONENT =====
const VibrantPill: FC<{
  title: string
  value: string | number
  bgColor: string
  icon: string
  isEditMode: boolean
  onDragStart: (e: React.DragEvent, index: number) => void
  onDragEnter: (index: number) => void
  onDragEnd: () => void
  index: number
  compact?: boolean
}> = ({ 
  title, 
  value, 
  bgColor, 
  icon, 
  isEditMode,
  onDragStart,
  onDragEnter,
  onDragEnd,
  index,
  compact = false
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [isHovered, setIsHovered] = useState(false)

  return (
    <div
      className={`vibrant-pill-wrapper ${isDragging ? 'dragging' : ''} ${isEditMode ? 'draggable' : ''}`}
      draggable={isEditMode}
      onDragStart={(e) => {
        setIsDragging(true)
        onDragStart(e, index)
      }}
      onDragEnd={() => {
        setIsDragging(false)
        onDragEnd()
      }}
      onDragEnter={() => onDragEnter(index)}
      onDragOver={(e) => e.preventDefault()}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        position: 'relative',
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: isHovered && !isDragging ? 'translateY(-2px)' : 'translateY(0)',
        zIndex: isDragging ? 100 : 1,
        opacity: isDragging ? 0.9 : 1,
        cursor: isEditMode ? 'grab' : 'default'
      }}
    >
      {/* Drag Handle - Visible only in edit mode */}
      {isEditMode && (
        <div style={{
          position: 'absolute',
          top: '-3px',
          right: '-3px',
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          background: '#ffffff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
          zIndex: 10,
          cursor: 'grab',
          border: `2px solid ${bgColor}`
        }}>
          <span style={{ fontSize: '10px', color: bgColor, lineHeight: 1, userSelect: 'none' }}>⋮⋮</span>
        </div>
      )}

      {/* Vibrant Pill */}
      <div style={{
        padding: compact ? '0.5rem 0.75rem' : '0.625rem 0.875rem',
        background: bgColor,
        border: `2px solid ${bgColor}`,
        borderRadius: '50px',
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        boxShadow: `0 4px 12px ${bgColor}80, 0 2px 4px rgba(0,0,0,0.15)`,
        position: 'relative',
        overflow: 'hidden',
        minHeight: compact ? '56px' : '64px',
        transition: 'all 0.2s ease'
      }}>
        <div style={{
          width: compact ? '32px' : '36px',
          height: compact ? '32px' : '36px',
          borderRadius: '50%',
          background: 'rgba(255,255,255,0.25)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0
        }}>
          <i className={`bi ${icon}`} style={{
            fontSize: compact ? '1rem' : '1.125rem',
            color: '#ffffff',
            textShadow: '0 1px 3px rgba(0,0,0,0.3)'
          }}></i>
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: compact ? '0.5625rem' : '0.625rem',
            fontWeight: 800,
            color: 'rgba(255,255,255,0.98)',
            textTransform: 'uppercase',
            letterSpacing: '0.3px',
            marginBottom: '0.0625rem',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}>
            {title}
          </div>
          <div style={{
            fontSize: compact ? '1.125rem' : '1.25rem',
            fontWeight: 900,
            color: '#ffffff',
            lineHeight: 1,
            textShadow: '0 1px 3px rgba(0,0,0,0.3)'
          }}>
            {value}
          </div>
        </div>

        <div style={{
          position: 'absolute',
          top: '5px',
          right: '8px',
          width: '5px',
          height: '5px',
          borderRadius: '50%',
          background: '#ffffff',
          animation: 'pulse 2s ease-in-out infinite',
          opacity: 0.95
        }}></div>
      </div>

      {isDragging && (
        <div style={{
          position: 'absolute',
          inset: '-2px',
          borderRadius: '52px',
          border: '2px dashed #ffffff',
          background: 'rgba(255,255,255,0.2)',
          pointerEvents: 'none',
          animation: 'pulse 0.8s ease-in-out infinite',
          zIndex: 5
        }}></div>
      )}

      {isHovered && isEditMode && !isDragging && (
        <div style={{
          position: 'absolute',
          inset: '-1px',
          borderRadius: '51px',
          border: '2px solid #ffffff',
          background: 'rgba(255,255,255,0.15)',
          pointerEvents: 'none',
          zIndex: 3
        }}></div>
      )}
    </div>
  )
}

interface Props {
  filter?: string
  isEditMode: boolean
  dateRange?: { start: string; end: string }
  endpoint?: string
  pollInterval?: number
  compact?: boolean
  isDark?: boolean
  themeColors?: {
    text?: string
    textMuted?: string
    bg?: string
    border?: string
  }
}

const RealtimeCounts = forwardRef(({
  filter = 'live',
  isEditMode,
  dateRange,
  endpoint = `${apiUrl}/dashboard/getcount`,
  pollInterval = 3000,
  compact = false,
  isDark = false,
  themeColors
}: Props, ref) => {
  const [data, setData] = useState<any>({
    agent_online: 0, answer: 0, no_answer: 0, busy: 0,
    failed: 0, call_attempt: 0, call_duration: 0, registered_ext: 0,
  })

  const [order, setOrder] = useState([
    { id: 'agent_online', title: 'Online', bg: '#2563eb', icon: 'bi-person-badge' },
    { id: 'answer', title: 'Answered', bg: '#059669', icon: 'bi-telephone-inbound' },
    { id: 'no_answer', title: 'No Answer', bg: '#dc2626', icon: 'bi-telephone-x' },
    { id: 'busy', title: 'Busy', bg: '#d97706', icon: 'bi-person-dash' },
    { id: 'agent_total', title: 'Extension', bg: '#7c3aed', icon: 'bi-cpu' },
    { id: 'call_attempt', title: 'Attempts', bg: '#0891b2', icon: 'bi-list-check' },
    { id: 'call_duration', title: 'Duration', bg: '#db2777', icon: 'bi-clock-history' },
    { id: 'failed', title: 'Failed', bg: '#64748b', icon: 'bi-exclamation-triangle' },
  ])

  const [originalOrder, setOriginalOrder] = useState([...order])
  const [connection, setConnection] = useState({ type: 'idle', ok: false })

  const esRef = useRef<any>(null)
  const pollRef = useRef<any>(null)
  const dragItem = useRef<number | null>(null)
  const dragOverItem = useRef<number | null>(null)

  useImperativeHandle(ref, () => ({
    cancelOrder: () => setOrder(originalOrder),
    saveCurrentOrder: () => setOriginalOrder([...order])
  }))

  const getToken = () => getApiToken()
  
  const appendToken = (url: string) => {
    const token = getToken()
    if (!token) return url
    return `${url}${url.includes('?') ? '&' : '?'}token=${encodeURIComponent(token)}`
  }

  const applyPayload = (payload: any) => {
    try {
      const parsed = typeof payload === 'string' ? JSON.parse(payload) : payload
      const normalize = (v: any) => (typeof v === 'string' ? Number(v) : v)
      
      setData((prev: any) => ({
        ...prev,
        agent_online: normalize(parsed.agent_online ?? prev.agent_online),
        answer: normalize(parsed.answer ?? prev.answer),
        no_answer: normalize(parsed.no_answer ?? prev.no_answer),
        busy: normalize(parsed.busy ?? prev.busy),
        failed: normalize(parsed.failed ?? prev.failed),
        call_attempt: normalize(parsed.call_attempt ?? prev.call_attempt),
        call_duration: normalize(parsed.call_duration ?? prev.call_duration),
        agent_total: normalize(parsed.agent_total ?? prev.agent_total),
      }))
    } catch (err) {
      console.error('Parse error:', err)
    }
  }

  const startSSE = (url: string) => {
    try {
      const es = new EventSource(url)
      esRef.current = es
      es.onopen = () => setConnection({ type: 'sse', ok: true })
      es.onmessage = (e) => applyPayload(e.data)
      es.onerror = () => {
        es.close()
        setConnection({ type: 'sse', ok: false })
      }
      return true
    } catch {
      return false
    }
  }

  const pollOnce = async (url: string) => {
    try {
      const res = await fetch(appendToken(url), {
        headers: { Authorization: `Bearer ${getToken()}` }
      })
      const json = await res.json()
      if (json.success) {
        applyPayload(json.data)
        setConnection({ type: 'poll', ok: true })
      }
    } catch {
      setConnection({ type: 'poll', ok: false })
    }
  }

  useEffect(() => {
    const cleanAll = () => {
      esRef.current?.close()
      if (pollRef.current) clearInterval(pollRef.current)
    }

    const start = () => {
      cleanAll()
      const sseUrl = appendToken(endpoint)
      if (!startSSE(sseUrl)) {
        pollOnce(endpoint)
        pollRef.current = setInterval(() => pollOnce(endpoint), pollInterval)
      }
    }

    start()
    return () => cleanAll()
  }, [endpoint])

  const handleSort = () => {
    if (dragItem.current === null || dragOverItem.current === null) return
    
    const _order = [...order]
    const draggedItemContent = _order.splice(dragItem.current, 1)[0]
    _order.splice(dragOverItem.current, 0, draggedItemContent)
    
    dragItem.current = null
    dragOverItem.current = null
    setOrder(_order)
  }

  const handleDragStart = (e: React.DragEvent, index: number) => {
    dragItem.current = index
    e.dataTransfer.effectAllowed = 'move'
  }

  const handleDragEnter = (index: number) => {
    dragOverItem.current = index
  }

  const handleDragEnd = () => {
    handleSort()
  }

  const getResponsiveClass = () => {
    if (compact) {
      return 'col-xl-3 col-lg-4 col-md-6 col-sm-6 col-6'
    }
    return 'col-xl-3 col-lg-4 col-md-6 col-sm-6 col-6'
  }

  const getDescription = () => {
    if (dateRange?.start && dateRange?.end) return `${dateRange.start} - ${dateRange.end}`
    return '● LIVE'
  }

  return (
    <>
      <div className="row g-2 vibrant-metrics-grid">
        {order.map((card, index) => (
          <div key={card.id} className={getResponsiveClass()}>
            <VibrantPill
              title={card.title}
              value={fmt(data[card.id])}
              bgColor={card.bg}
              icon={card.icon}
              isEditMode={isEditMode}
              onDragStart={handleDragStart}
              onDragEnter={handleDragEnter}
              onDragEnd={handleDragEnd}
              index={index}
              compact={compact}
            />
          </div>
        ))}
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.05); }
        }
        
        .vibrant-pill-wrapper {
          will-change: transform;
        }
        .vibrant-pill-wrapper.dragging {
          cursor: grabbing !important;
          z-index: 1000 !important;
        }
        .vibrant-pill-wrapper.draggable:hover {
          cursor: grab;
        }
        .vibrant-pill-wrapper.draggable:active {
          cursor: grabbing;
        }
        
        .vibrant-metrics-grid {
          display: flex;
          flex-wrap: wrap;
          margin: 0 -0.375rem;
        }
        .vibrant-metrics-grid > [class*='col-'] {
          padding: 0.375rem;
        }
        
        /* Responsive breakpoints */
        @media (min-width: 1400px) {
          .vibrant-metrics-grid > .col-xl-3 {
            flex: 0 0 12.5%;
            max-width: 12.5%;
          }
        }
        @media (max-width: 1399px) and (min-width: 1200px) {
          .vibrant-metrics-grid > .col-lg-4 {
            flex: 0 0 25%;
            max-width: 25%;
          }
        }
        @media (max-width: 1199px) and (min-width: 992px) {
          .vibrant-metrics-grid > .col-md-6 {
            flex: 0 0 33.333%;
            max-width: 33.333%;
          }
        }
        @media (max-width: 991px) and (min-width: 768px) {
          .vibrant-metrics-grid > .col-sm-6 {
            flex: 0 0 50%;
            max-width: 50%;
          }
        }
        @media (max-width: 767px) {
          .vibrant-metrics-grid > .col-6 {
            flex: 0 0 50%;
            max-width: 50%;
          }
        }
        @media (max-width: 480px) {
          .vibrant-metrics-grid > [class*='col-'] {
            flex: 0 0 50%;
            max-width: 50%;
          }
        }
      `}</style>
    </>
  )
})

export { RealtimeCounts }